import { useCallback, useState } from 'react';

import type { Image } from '../types';

export interface ImageSelection {
    /** Index into `images` of the image on screen. 0 when the list is empty. */
    index: number;
    image: Image | null;
    isFirst: boolean;
    isLast: boolean;
    selectById: (id: string) => void;
    step: (direction: 'next' | 'previous') => void;
}

/**
 * Which image the viewer is showing.
 *
 * Tracked by id rather than index, so a new `images` array holding the same images never
 * moves the viewer. Comparing array references used to send the user back to the first
 * image of the day on every parent re-render (WB-2143) - submitting a feedback note was
 * enough to trigger it.
 *
 * The index is *derived*, with no state written during render:
 *   - nothing selected yet     -> initialImageIndex, so permalinks resolve to their image
 *   - selected id in the list  -> its position
 *   - selected id has gone     -> 0, i.e. the first image of the new day or filter
 *
 * The previous version reached the same three outcomes by calling setSelectedImageId in
 * the render body, which is the single hardest thing in this component to reason about.
 * Deriving it means the state only ever changes in response to the user actually
 * navigating.
 */
export function useImageSelection(images: Image[], initialImageIndex = 0): ImageSelection {
    const [selectedImageId, setSelectedImageId] = useState<string | undefined>(undefined);

    const foundIndex = images.findIndex((image) => image.id === selectedImageId);
    const seedIndex = selectedImageId === undefined ? initialImageIndex : 0;
    const lastIndex = Math.max(images.length - 1, 0);
    const index = foundIndex === -1 ? Math.min(Math.max(seedIndex, 0), lastIndex) : foundIndex;

    const step = useCallback(
        (direction: 'next' | 'previous') => {
            setSelectedImageId((currentId) => {
                const currentIndex = images.findIndex((image) => image.id === currentId);
                const from = currentIndex === -1 ? seedIndex : currentIndex;
                const to = direction === 'next' ? from + 1 : from - 1;

                // No wrapping at either end.
                if (to < 0 || to > images.length - 1) {
                    return currentId;
                }
                return images[to].id;
            });
        },
        [images, seedIndex]
    );

    return {
        index,
        image: images[index] ?? null,
        isFirst: index === 0,
        isLast: index === images.length - 1,
        selectById: setSelectedImageId,
        step,
    };
}
